import { useSEOHead } from "@/hooks/useSEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import TrustStats from "./components/TrustStats";
import CategoryGrid from "./components/CategoryGrid";
import WhatIsSection from "./components/WhatIsSection";
import HowItWorks from "./components/HowItWorks";
import ComparisonTable from "./components/ComparisonTable";
import BenefitsSection from "./components/BenefitsSection";
import SafetySection from "./components/SafetySection";
import ResourcesSection from "./components/ResourcesSection";
import CTABanner from "./components/CTABanner";
import FAQReviews from "./components/FAQReviews";

const HOME_TITLE = "Mulebuy Spreadsheet Guide 2026 - The Most Complete Spreadsheet Shopping Resource | MulebuySpreadsheets";
const HOME_DESC = "Discover the Mulebuy spreadsheet shopping system: browse 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, and Accessories. Learn how spreadsheet-based shopping saves time with transparent pricing and organized product listings. Your complete Mulebuy spreadsheet guide for 2026.";
const HOME_KEYWORDS = "Mulebuy spreadsheet, spreadsheet shopping, mulebuy spreadsheet guide, product spreadsheet, online shopping tool, spreadsheet system, shopping categories, mulebuy spreadsheet 2026";
const HOME_IMAGE = "https://readdy.ai/api/search-image?query=Aesthetic%20flat%20lay%20photography%20of%20diverse%20fashion%20accessories%20including%20sunglasses%20a%20leather%20watch%20folded%20denim%20jacket%20and%20canvas%20sneakers%20arranged%20artistically%20on%20warm%20terracotta%20toned%20linen%20fabric%20surface%2C%20soft%20diffused%20natural%20window%20light%20casting%20gentle%20shadows%2C%20editorial%20composition%20with%20negative%20space%2C%20warm%20earthy%20color%20palette%20with%20sandy%20beige%20and%20rust%20undertones%2C%20calm%20minimalist%20lifestyle%20aesthetic&width=1800&height=1200&seq=hero-bg-v2-001&orientation=landscape";

const FAQ_ITEMS = [
  {
    question: "What is a Mulebuy spreadsheet?",
    answer: "A Mulebuy spreadsheet is an organized product listing system that compiles thousands of items across 14 categories into a single structured view. It helps shoppers compare prices, browse products efficiently, and make informed purchasing decisions without jumping between multiple websites.",
  },
  {
    question: "How does the Mulebuy spreadsheet system work?",
    answer: "The Mulebuy spreadsheet organizes products by category with consistent information — product name, price, and a direct link. Shoppers browse the spreadsheet to discover and compare items, then click through to the main platform at kaspreadsheet.com to view details and complete purchases.",
  },
  {
    question: "Is the Mulebuy spreadsheet safe to use?",
    answer: "Yes — the Mulebuy spreadsheet is an informational tool only. It does not process payments or collect personal data. All actual transactions happen on the secure main platform at kaspreadsheet.com. Browsing the spreadsheet itself carries no financial risk.",
  },
  {
    question: "What product categories does the Mulebuy spreadsheet cover?",
    answer: "The Mulebuy spreadsheet covers 14 categories: Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and OtherStuff — providing comprehensive coverage for fashion and lifestyle shopping.",
  },
  {
    question: "Is the Mulebuy spreadsheet free?",
    answer: "Yes, the Mulebuy spreadsheet is a free informational resource. You can browse all 14 categories and view product listings without any cost. Purchases are made separately on the main platform at kaspreadsheet.com.",
  },
];

export default function Home() {
  const baseUrl = (() => {
    try {
      const env = (import.meta as Record<string, { VITE_SITE_URL?: string }>).env;
      return (env.VITE_SITE_URL || "https://mulebuyspreadsheets.hair").replace(/\/+$/, "");
    } catch {
      return "https://mulebuyspreadsheets.hair";
    }
  })();

  useSEOHead({
    title: HOME_TITLE,
    description: HOME_DESC,
    keywords: HOME_KEYWORDS,
    canonicalPath: "/",
    ogImage: HOME_IMAGE,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "MulebuySpreadsheets",
        url: baseUrl,
        description: "Your trusted informational hub for Mulebuy spreadsheet insights, comparisons, and shopping guides.",
        potentialAction: {
          "@type": "SearchAction",
          target: `${baseUrl}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  });

  return (
    <main>
      <Navbar />
      <HeroSection />
      <TrustStats />
      <CategoryGrid />
      <WhatIsSection />
      <HowItWorks />
      <ComparisonTable />
      <BenefitsSection />
      <SafetySection />
      <ResourcesSection />
      <FAQReviews />
      <CTABanner />
      <Footer />
    </main>
  );
}